export default {
  settings: {
    title: 'Settings',
    account: 'Account',
    appSettings: 'App Settings',
    dataPrivacy: 'Data & Privacy',
    emptyProfile: 'No profile data available. Complete the onboarding flow to populate your profile.',
    emptyState: {
      title: 'We couldn’t detect any saved profile data.',
      body: 'Run the onboarding assistant so quest scheduling has the inputs it needs.',
    },
    appLanguage: {
      title: 'App Language',
      description: 'Choose the default app and AI language.',
      save: 'Save Language',
      saving: 'Saving...',
      updated: 'Language preference updated.',
      error: 'Failed to update language preference.',
    },
    metricUnits: 'Metric (kg, km)',
    managePlan: 'Manage plan',
    levelBadge: 'Level {level}',
    profileSection: 'Profile',
    trainingSection: 'Training',
    nutritionSection: 'Nutrition',
    equipmentSection: 'Equipment',
    bodyCompositionSection: 'Body composition',
    healthNutritionSection: 'Health & nutrition',
    profileSnapshot: 'Profile Snapshot',
    preferencesSection: 'Preferences',
    fullExtractedProfile: 'Full Extracted Profile',
    completeOnboardingFirst: 'Complete onboarding first, then come back here to edit your saved profile.',
    profileUpdatedNoQuestChange: 'Profile updated. Quests remain unchanged.',
    labels: {
      fullName: 'Full name',
      age: 'Age',
      gender: 'Gender',
      location: 'Location',
      height: 'Height',
      weight: 'Weight',
      exerciseFamiliarity: 'Exercise familiarity',
      weeklyActivity: 'Weekly activity',
      sessionsPerWeek: 'Sessions per week',
      preferredWorkoutDays: 'Preferred workout days',
      fitnessGoals: 'Fitness goals',
      workoutStyles: 'Workout styles',
      preferredUnits: 'Preferred units',
      availableEquipment: 'Available equipment',
      notes: 'Notes',
      medicalConditions: 'Medical conditions, injuries, or pain',
      dietaryPreferences: 'Dietary preferences',
      allergies: 'Allergies',
    },
    measurements: {
      neck: 'Neck',
      chest: 'Chest',
      waist: 'Waist',
      hips: 'Hips',
      thigh: 'Thigh',
    },
    training: {
      sessionsPerWeekValue: '{count} sessions / week',
    },
    nutrition: {
      allergiesValue: 'Allergies: {value}',
    },
    actions: {
      rescheduleTitle: 'Reschedule Quests',
      rescheduleDescription: 'Adjust your profile inputs and start a fresh quest plan when you are ready.',
      editProfile: 'Edit Profile',
      editingProfile: 'Editing Profile',
      editProfileDescription: 'Update your details and preferences without regenerating quests automatically.',
    },
    push: {
      title: 'Push Notifications',
      needsSetup: 'Notifications were requested, but this iPhone still needs to sync a valid push token.',
      permissionDenied: 'Notifications are blocked in iPhone Settings.',
      enabledDescription: 'Quest, streak, leaderboard, and duel notifications are enabled on this iPhone.',
      disabledDescription: 'Quest, streak, leaderboard, and duel notifications are disabled on this iPhone.',
      iosOnly: 'Push notifications are currently available in the iOS app only.',
      enabledFeedback: 'Push notifications enabled.',
      disabledFeedback: 'Push notifications disabled.',
      disabledInSettings: 'Push notifications were disabled in iPhone Settings.',
      deniedHelp: 'Notifications are disabled in iPhone Settings. Enable them there, then try again.',
      updateFailed: 'Failed to update push notifications.',
      sendTest: 'Send Test Push',
      sendingTest: 'Sending test push...',
      testDescription: 'Sends a one-off notification to this iPhone and reports the current Expo delivery state.',
      lastTest: 'Last test result',
      ticketId: 'Ticket ID',
      providerError: 'Provider error',
      testDelivered: 'Test notification delivered successfully.',
      testPending: 'Test notification accepted. Receipt confirmation is still pending.',
      testFailed: 'Failed to send test push notification.',
      testStatusDelivered: 'Delivered',
      testStatusPending: 'Pending',
      testStatusAccepted: 'Accepted',
      testStatusNotReady: 'Not ready',
      testStatusError: 'Error',
      openSettings: 'Open iPhone Settings',
    },
    healthKit: {
      title: 'HealthKit Verification',
      description: 'Manage iOS HealthKit access for steps, sleep, and active-calorie quest verification.',
    },
    guideRestore: {
      title: 'Restore Feature Guide',
      description: 'Reopen the dashboard walkthrough and product tips.',
      successTitle: 'Success',
      successBody: 'Feature guide restored. Check your Dashboard.',
    },
    aboutForza: {
      title: 'About Forza',
      description: 'Open the public guide, product overview, and launch information.',
    },
    healthSources: {
      title: 'Health Sources',
      description: 'See where Forza health tips and planning defaults come from, with links to original sources.',
    },
    support: {
      title: 'Support',
      description: 'Get help and contact us directly.',
    },
    logout: {
      title: 'Logout',
      description: 'Sign out of your account on this device.',
    },
    download: {
      title: 'Download My Data',
      description: 'Export your profile, settings, and quest history.',
      started: 'Data download started.',
      failed: 'Failed to download data.',
    },
    delete: {
      title: 'Delete Account',
      description: 'Permanently delete your account and anonymize all linked data.',
      footnote: 'Deleting your account anonymizes your profile and removes HealthKit snapshots. Audit logs remain anonymized for up to 12 months.',
      modalTitle: 'Delete your account',
      modalBody: 'This will permanently delete your account. Your email, username, and profile data will be anonymized. HealthKit snapshots are removed. Anonymized logs are retained for up to 12 months for audit and fraud prevention.',
      confirmLabel: 'Type DELETE to confirm',
      confirmPlaceholder: 'DELETE',
      cancel: 'Cancel',
      confirm: 'Delete Account',
      deleting: 'Deleting...',
      typeDelete: 'Type DELETE to confirm account deletion.',
      failed: 'Failed to delete account. Please try again.',
    },
    plan: {
      limitReached: 'Quest regeneration limit reached.',
      alreadyRunning: 'Quest plan regeneration is already running.',
      failedDefault: 'Quest regeneration did not complete. Please retry when ready.',
      inProgressTitle: 'Quest plan regeneration is in progress.',
      triggeredBy: 'Triggered by {source}',
      triggeredBySystem: 'Triggered by system',
      startedAt: 'Started at {time}',
      autoUpdate: 'The dashboard will update automatically.',
      failedTitle: 'Quest plan regeneration failed.',
      lastAttempt: 'Last attempt finished at {time}. Retry when ready.',
      rescheduling: 'Rescheduling...',
      rescheduleFailed: 'Reschedule failed',
      rescheduleComplete: 'Reschedule complete',
    },
    assessment: {
      title: 'Fitness Assessment',
      completed: 'Completed',
      skipped: 'Skipped',
      available: 'Available',
      description: 'Complete this one-time self-report baseline to recalibrate future quest difficulty. If your current level is higher, your earned level is preserved.',
      close: 'Close assessment',
      start: 'Start assessment',
      assignedLevel: 'Assigned level',
      submitted: 'Submitted',
      qualityHeadline: 'Choose controlled movement quality over maximum numbers.',
      qualityBody: 'Pick the hardest variation you can perform safely and enter the result. Submission is one-time.',
      resultPlaceholder: 'Result',
      safetyNote: 'This assessment is self-reported and is not medical clearance. Stop if anything feels wrong.',
      submit: 'Submit one-time assessment',
      submitting: 'Submitting assessment...',
      submittedFeedback: 'Fitness assessment submitted.',
      submitFailed: 'Failed to submit fitness assessment.',
      raiseLevelConfirm: 'Submitting this one-time assessment will raise your starting baseline and regenerate future incomplete quests. Continue?',
    },
    errors: {
      save: 'An error occurred while saving.',
    },
  },
  appShell: {
    loading: 'Loading...',
  },
  playerStats: {
    locked: 'Locked',
    syncedAt: 'Synced {time}',
    greeting: 'Hi {name}',
    timeLeft: '{time} left',
    nutritionToday: 'Nutrition Today',
    caloriesBurned: 'Calories Burned',
    notSynced: 'Not synced',
    workoutVerified: 'Workout verified',
    workoutInProgress: 'Workout in progress',
    activeEnergyToday: 'Active energy today',
    healthKitStepsTitle: '10,000 Steps Goal',
    healthKitSleepTitle: '7 Hours Sleep Goal',
    healthKitPrompt: 'Enable HealthKit verification in Settings on iOS to track today&apos;s steps, sleep, and active calories.',
    healthKitSyncHint: 'Sync happens on app open and when returning to the foreground (max every 15 minutes).',
    healthKitEnableStatus: 'Enable HealthKit verification in Settings on iPhone',
    healthKitCompleted: 'Completed',
    healthKitWaiting: 'Waiting for HealthKit sync',
    healthKitInProgress: 'In progress',
  },
  coachLocked: {
    title: 'AI Coach',
    subtitle: 'Ultra-only feature',
    upgrade: 'Upgrade',
  },
  auth: {
    loginTitle: 'Welcome back',
    loginSubtitle: 'Pick up where you left off.',
    email: 'Email address',
    password: 'Password',
    signIn: 'Sign in',
    continueWith: 'Or continue with',
    signInWithApple: 'Sign in with Apple',
    signInWithGoogle: 'Sign in with Google',
    createAccount: 'New here? Create your account',
    registerTitle: 'Create your account',
    registerSubtitle: 'Set up your profile and unlock your first plan.',
    username: 'Username',
    confirmPassword: 'Confirm password',
    register: 'Create account',
    signUpWithApple: 'Sign up with Apple',
    signUpWithGoogle: 'Sign up with Google',
    alreadyHaveAccount: 'Already have an account? Sign in',
    inviteOnlyTitle: 'Early access is still curated',
    inviteOnlySubtitle: 'We are onboarding new members gradually so the experience stays high-touch and the coaching stays sharp.',
    needAccess: 'Need access? Apply for early access',
    alreadyApproved: 'Already approved?',
    approvedEmailPrompt: 'Use the email that received approval to unlock registration.',
    approvedEmailPlaceholder: 'Approved email',
    checking: 'Checking...',
    continue: 'Continue',
    unlockReady: 'Registration is ready to unlock',
    unlockReadyDescription: 'Your approved email is about to open registration. Complete signup right away so the unlock does not expire.',
    registrationUnlocked: 'Registration unlocked',
    registrationUnlockedDescription: 'This approval opens registration for a short window. Completing account creation will consume it permanently.',
    approvedEmail: 'Approved email: {email}',
    termsAgreement: 'I have read and agree to the Terms of Service and Privacy Policy.',
    ageConfirm: 'I confirm that I am at least 16 years old.',
    aiConsent: 'I consent to the required AI data processing.',
    viewDetails: 'View details',
    consentTag: 'Data & Privacy',
    consentTitle: 'AI Data Processing',
    consentProcessed: 'Data that may be processed',
    consentProviders: 'AI and speech providers',
    consentInfrastructure: 'Infrastructure providers',
    consentWhenSent: 'When data is sent',
    consentAgree: 'Agree and continue',
    consentDecline: 'Decline',
    consentClose: 'Close AI data processing details',
    consentIntro:
      'Forza uses trusted AI and speech providers to generate personalized quests, coach you, verify completions, review exercise form, and power related AI features.',
    consentProcessedItems: {
      profile: 'Fitness goals, preferences, profile context, and onboarding answers.',
      quests: 'Quest plans, quest activity, completion status, and verification context.',
      text: 'Text you submit to AI-powered features.',
      voice: 'Voice recordings and transcripts submitted to Voice Coach or voice verification.',
      photos: 'Photos submitted for meal or quest verification.',
      exercise: 'Exercise frames submitted for form checks.',
      diagnostics: 'App, device, and diagnostic context needed to operate, secure, and troubleshoot these features.',
    },
    consentProvidersBody:
      'Forza may use Google, Groq, OpenRouter, DeepSeek, OpenAI, and local or on-device providers where available.',
    consentInfrastructureBody:
      'Related data may also be handled by providers used for hosting, storage, media delivery, monitoring, subscriptions, advertising consent and reward attribution, and security, including Render, MongoDB Atlas, Cloudflare R2, ImageKit, Better Stack, Apple, RevenueCat, and Google AdMob where relevant.',
    consentWhenSentItems: {
      onboarding: 'During onboarding and quest generation.',
      coach: 'When you use AI Coach or Voice Coach.',
      verification: 'When you submit voice notes, photos, or exercise frames for verification or form checks.',
      planning: 'When AI-powered features need app context to respond, verify progress, or plan quests.',
    },
    consentFooter:
      "Forza does not sell your personal data or share it for third-party advertising or profiling. These providers process data only as needed to operate Forza's services and must provide the same or equal protection described in our Privacy Policy.",
    unexpectedError: 'Something went wrong. Please try again.',
    noAccountFound: 'No account found. Please register first to create one.',
    authCreateFailed: 'Authentication failed while creating the account. Please try again.',
    accountDeleted: 'This account has been deleted.',
    appleMissingEmail: 'Apple Sign In did not provide an email address for this account.',
    appleUnavailable: 'Apple Sign In is unavailable in this environment.',
    appleUnavailableDetailed:
      'Apple Sign In is unavailable on this simulator or device. Test on a real iPhone if the simulator cannot complete Apple authentication.',
    appleInternalError:
      'Apple Sign In reached Forza but the server could not verify the Apple token. Check backend logs and Apple bundle configuration.',
    appleInvalidIdentity: 'Apple Sign In did not provide a valid account identifier. Please try again.',
    authFailed: 'Authentication failed. Please try again.',
    googleLoginFailed:
      'No account was found. Create an account first, then sign in.',
    registerRequirements:
      'To create your account, you need to accept the Terms and Privacy Policy, confirm you are at least 16, and consent to the required AI data processing.',
    registerSuccess:
      'Account created. Verify your email before signing in.',
    verificationSentTitle: 'Verification email sent',
    verificationSentBody:
      'We sent a verification link to {email}. Open the link to confirm your email and activate your account. Check your inbox and spam or junk folder.',
    verificationInitialFailure:
      'Your account was created, but the verification email could not be sent. Request another email below.',
    verificationResend: 'Resend verification email',
    verificationResending: 'Sending...',
    verificationRetryCountdown: 'You can request another email in {seconds} seconds.',
    verificationResendError: 'Unable to send another verification email right now.',
    backToSignIn: 'Back to sign in',
    unlockApprovalFound: 'Approval found. Confirm to unlock registration.',
    unlockConsumed: 'This approved email has already been used to create an account.',
    unlockMissing: 'This email is not currently approved for early access.',
    unlockCheckFailed: 'Unable to check approval status right now.',
    unlockIssueFailed: 'Unable to unlock registration right now.',
    unlockCta: 'Unlock registration',
    approvedEmailWarningTitle: 'Use approved email',
    approvedEmailWarningBody:
      'This approved email is about to unlock registration. Create your account right away so the unlock does not expire.',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    backToAboutForza: 'Back to About Forza',
  },
  landingIntro: {
    startAria: 'Start Forza intro',
    subprompt: 'A longevity app',
    loading: 'Loading intro...',
    enter: 'Tap to enter Forza',
    preparing: 'Preparing video...',
  },
  googleCallback: {
    processing: 'Processing login...',
  },
  onboarding: {
    setupTag: 'Forza setup',
    modeChoiceTitle: 'Choose your setup pace.',
    modeChoiceDescription:
      'Fast setup gets you moving quickly. Detailed setup gives Forza more nuance so your first plan feels sharper from day one.',
    fastTitle: 'Fast setup',
    fastDescription:
      'A focused setup that captures the essentials and gets your first plan ready fast.',
    fastBestFor: 'Best when you want momentum now.',
    detailedTitle: 'Detailed setup',
    detailedDescription:
      'A deeper setup with body measurements, workout detail, and app preferences. Units default to metric and can be changed later in Settings.',
    detailedBestFor: 'Best when you want a more personalized start.',
    loading: 'Loading your onboarding profile...',
    changePath: 'Change setup path',
    progress: 'Progress',
    step: 'Step {current}',
    stepOf: 'Step {current} of {total}',
    fullName: 'Full name',
    age: 'Age',
    height: 'Height',
    weight: 'Weight',
    gender: 'Gender',
    location: 'Location',
    preferredUnits: 'Preferred units',
    options: {
      gender: {
        Male: 'Man',
        Female: 'Woman',
        Other: 'Other',
      },
      activity: {
        '1-2 days per week': '1-2 days a week',
        '3-4 days per week': '3-4 days a week',
        '5-6 days per week': '5-6 days a week',
        '7 days per week': '7 days a week',
      },
      dietary: {
        Vegan: 'Vegan',
        Vegetarian: 'Vegetarian',
        'Gluten-Free': 'Gluten-free',
        'Low-Carb': 'Lower-carb',
        Keto: 'Keto',
      },
      workout: {
        'Strength Training': 'Strength training',
        Cardio: 'Cardio',
        Yoga: 'Yoga',
        Pilates: 'Pilates',
        HIIT: 'HIIT',
        Cycling: 'Cycling',
        Running: 'Running',
        Meditation: 'Meditation',
      },
      equipment: {
        None: 'No equipment',
        Dumbbells: 'Dumbbells',
        'Resistance bands': 'Resistance bands',
        Kettlebell: 'Kettlebell',
        'Pull-up bar': 'Pull-up bar',
        'Gym access': 'Gym access',
      },
      familiarity: {
        Novice: 'Just getting started',
        Basic: 'I know the basics',
        Comfortable: 'I train comfortably',
        Advanced: 'I train with confidence',
        'Expert/PT': 'Expert / coach level',
      },
      familiarityDescriptions: {
        Novice: 'Keep it simple, clear, and beginner-friendly.',
        Basic: 'I know the main patterns and prefer straightforward sessions.',
        Comfortable: 'I can handle a solid range of common exercises.',
        Advanced: 'I am comfortable with more technical variations and structured sessions.',
        'Expert/PT': 'I understand programming, form, and advanced progressions.',
      },
      units: {
        'Metric (kg, km)': 'Metric (kg, km)',
        'Imperial (lbs, miles)': 'Imperial (lbs, miles)',
      },
      weekdays: {
        Mon: 'Mon',
        Tue: 'Tue',
        Wed: 'Wed',
        Thu: 'Thu',
        Fri: 'Fri',
        Sat: 'Sat',
        Sun: 'Sun',
      },
    },
    enterFullName: 'Enter your full name',
    enterAge: 'Enter your age',
    enterHeight: 'Enter your height',
    enterWeight: 'Enter your weight',
    cityCountry: 'City, Country',
    sessionsPerWeek: 'Sessions per week',
    preferredWorkoutDays: 'Preferred workout days',
    preferredWorkoutDaysCount: '{selected}/{total} selected',
    workoutDayHint: 'The planner will generate workout quests only on these weekdays. Meals and mindfulness still appear every day.',
    trainingContextTitle: 'Training context',
    trainingContextDescription:
      'This helps Forza match your first week to your experience, confidence, and real training rhythm.',
    weeklyActivity: 'Weekly activity',
    fitnessGoals: 'What are your fitness goals?',
    fitnessGoalsPlaceholder: 'e.g., Build strength, lose fat, feel athletic again...',
    tellUsAboutYourself: 'Tell us about yourself',
    basicsDescription: 'These details shape your plan, pace, and how Forza talks to you.',
    bodyTitle: 'Measure your frame',
    bodyDescription: 'All circumference fields are optional. You can leave every field blank and continue to the next onboarding step.',
    optionalNotes: 'Optional notes',
    optionalNotesPlaceholder: 'Any asymmetry, current focus, or measurement context.',
    dietaryPreferences: 'Dietary preferences',
    allergiesToggle: 'I have allergies',
    allergiesPlaceholder: 'List allergies or reactions.',
    safetyNote: 'Safety note',
    safetyDescription: 'We will use this to keep your plan sensible. It is guidance for planning, not medical clearance.',
    medicalConditions: 'Medical conditions, injuries, or pain',
    medicalConditionsPlaceholder: 'e.g., Asthma, knee pain, previous shoulder injury, or None.',
    preferencesTitle: 'Tailor the experience',
    preferencesDescription: 'Shape the experience so reminders, workout style, and equipment match real life.',
    pushNotifications: 'Push notifications',
    pushNotificationsDescription: 'Receive reminders and quest updates.',
    openIphoneSettings: 'Open iPhone Settings',
    favouriteWorkoutStyles: 'Favourite workout styles',
    equipmentYouOwn: 'Equipment you own',
    addCustomEquipment: 'Add custom equipment',
    add: 'Add',
    reviewTitle: 'Review your setup',
    reviewDescription: 'Take one last look, then decide whether to calibrate your starting level before the first plan is generated.',
    reviewProfile: 'Profile',
    reviewTraining: 'Training',
    reviewEquipment: 'Equipment',
    reviewHealth: 'Health & nutrition',
    notProvided: 'Not provided',
    none: 'None',
    name: 'Name',
    goal: 'Goal',
    familiarity: 'Familiarity',
    workoutDays: 'Workout days',
    workoutStyles: 'Workout styles',
    units: 'Units',
    availableEquipment: 'Available equipment',
    healthNotes: 'Health notes',
    assessmentTitle: 'Optional fitness assessment',
    assessmentDescription:
      'Use this quick self-check to fine-tune your starting level. If you skip it, Forza will start more conservatively.',
    startAssessment: 'Start assessment',
    startAssessmentDescription: 'Pick exercise variations and enter your result for six home-friendly domains.',
    skipForNow: 'Skip for now',
    skipForNowDescription: 'Generate conservative starter quests now and keep the assessment available in Settings.',
    selfReportTitle: 'Self-report fitness test',
    selfReportDescription:
      'Pick the hardest variation you can own with clean form, then log your result where needed. Forza uses it to set a smarter baseline.',
    resultPlaceholder: 'Result',
    assessmentSafety:
      'This is self-reported and should be performed only if you feel safe doing so. Stop if anything feels wrong.',
    back: 'Back',
    next: 'Next',
    saving: 'Saving...',
    continueToAssessment: 'Continue to assessment',
    generatingQuests: 'Generating quests...',
    submitAssessment: 'Submit assessment & generate quests',
    fastCoachIntro: "Let's keep this quick. First, I need the essentials so your first plan lands in the right zone.",
    fastGoalPrompt: 'What should your first plan help you achieve most right now?',
    fastGoalPlaceholder:
      'e.g., I want to lose fat without losing strength, train 4 days a week, and improve consistency.',
    fastFamiliarityPrompt: 'How confident do you feel with exercise technique and movement variety?',
    fastAvailabilityPrompt: 'In a real week, how often can you show up for training or recovery work?',
    fastEquipmentPrompt: 'What equipment can Forza count on you having available?',
    fastHealthPrompt: 'Any injuries, pain points, or limitations your plan should work around?',
    fastHealthPlaceholder: 'Write None if there is nothing to flag.',
    fastHealthHint:
      'If you report a concern, Forza will still continue but your plan should stay conservative around it.',
    fastNutritionPrompt: 'Last one: are there nutrition preferences or allergies meal quests should respect?',
    errors: {
      loadProfile: 'Failed to load your profile. Please refresh and try again.',
      saveProgress: 'Failed to save onboarding progress.',
      finalize: 'Failed to finalise onboarding. Please try again.',
      workoutDaysExact: 'Choose exactly {count} workout day{suffix} before continuing.',
      pushDisabledExternal:
        'Push notifications were disabled in iPhone Settings. Enable them there if you want reminders from Forza.',
      pushDenied: 'Notifications are disabled in iPhone Settings. Turn them on there, then try again.',
      pushConfig: 'Failed to configure push notifications.',
    },
    steps: {
      chooseSetup: 'Choose Setup',
      personalInfo: 'Create Your Profile',
      fitnessProfile: 'Training Context',
      bodyComposition: 'Body Measurements',
      healthDetails: 'Health Essentials',
      preferences: 'App Preferences',
      review: 'Review & Confirm',
      assessment: 'Fitness Assessment',
      fastBasics: 'Basics',
      fastGoal: 'Goal',
      fastFamiliarity: 'Exercise Familiarity',
      fastAvailability: 'Weekly Availability',
      fastEquipment: 'Equipment',
      fastHealth: 'Health Notes',
      fastNutrition: 'Nutrition Notes',
      fastReview: 'Review',
    },
    bodyLabels: {
      neck: 'Neck',
      chest: 'Chest',
      waist: 'Waist',
      hips: 'Hips',
      thigh: 'Thigh',
      circumference: '{label} circumference',
    },
    assessmentDomains: {
      push: {
        label: 'Push',
        prompt: 'How many controlled reps can you complete?',
        variations: {
          easier: 'Incline push-up',
          standard: 'Push-up',
          harder: 'Decline push-up',
        },
        variationDescriptions: {
          easier: 'Hands on a wall, bench, or sturdy raised surface. Lower with control, then press back up.',
          standard: 'Hands on the floor with your body in a straight line. Lower under control and press up without the hips sagging.',
          harder: 'Feet elevated and hands on the floor. Keep the body rigid while lowering and pressing back up.',
        },
      },
      squat: {
        label: 'Squat / lunge',
        prompt: 'How many controlled reps can you complete?',
        variations: {
          easier: 'Box squat',
          standard: 'Air squat',
          harder: 'Reverse lunge',
        },
        variationDescriptions: {
          easier: 'Sit back to a chair or box, lightly touch it, then stand back up under control.',
          standard: 'Feet about shoulder-width apart. Lower into a bodyweight squat and stand without bouncing.',
          harder: 'Step one leg back into a lunge, lower with control, then return to standing before switching sides.',
        },
      },
      hinge: {
        label: 'Hinge',
        prompt: 'How many controlled reps can you complete?',
        variations: {
          easier: 'Glute bridge',
          standard: 'Hip hinge good morning',
          harder: 'Single-leg Romanian deadlift',
        },
        variationDescriptions: {
          easier: 'Lie on your back with feet planted and lift your hips by squeezing your glutes.',
          standard: 'Stand tall, push the hips back with a long spine, then return upright without rounding.',
          harder: 'Balance on one leg, hinge from the hips with control, then return tall without twisting.',
        },
      },
      core: {
        label: 'Core',
        prompt: 'How long can you hold steady form?',
        variations: {
          easier: 'Knee plank',
          standard: 'Forearm plank',
          harder: 'Long-lever plank',
        },
        variationDescriptions: {
          easier: 'Hold a straight line from shoulders to knees while keeping the trunk braced.',
          standard: 'Hold a forearm plank with level hips and your body in one straight line.',
          harder: 'Plank with the elbows farther forward to increase leverage while keeping full-body tension.',
        },
      },
      cardio: {
        label: 'Cardio',
        prompt: 'How many minutes can you sustain a challenging but controlled effort?',
        variations: {
          easier: 'Brisk walk',
          standard: 'Jog or step-ups',
          harder: 'Fast run or burpees',
        },
        variationDescriptions: {
          easier: 'Walk at a pace that clearly raises breathing but still feels controlled.',
          standard: 'Choose an easy jog or repeated step-ups that feel challenging but sustainable.',
          harder: 'Choose fast running or burpees only if you can keep the effort controlled and safe.',
        },
      },
      mobility: {
        label: 'Mobility range',
        prompt: 'Choose the closest pain-free movement range you can control today.',
        variations: {
          very_limited: 'Very limited or painful',
          limited: 'Limited, cautious range',
          standard: 'Normal daily movement',
          deep_controlled: 'Deep range with control',
          deep_unrestricted: 'Deep range, no discomfort',
        },
        variationDescriptions: {
          very_limited: 'Movement is restricted or painful early, so you need to stop well before a normal range.',
          limited: 'You can move a bit, but range is clearly reduced and needs caution.',
          standard: 'You can manage ordinary daily positions and ranges without major restriction.',
          deep_controlled: 'You can access a large range while staying stable and in control.',
          deep_unrestricted: 'You can move through a deep range comfortably, without pain or obvious compensation.',
        },
      },
    },
    assessmentDifficulty: {
      easier: 'Easier',
      standard: 'Standard',
      harder: 'Harder',
      very_limited: 'Very limited',
      limited: 'Limited',
      deep_controlled: 'Deep',
      deep_unrestricted: 'Full',
    },
    assessmentUnits: {
      reps: 'reps',
      seconds: 'seconds',
      minutes: 'minutes',
      choice: 'choice',
    },
  },
  dashboard: {
    dailyQuests: 'Daily Quests',
    recoveryModeActive: 'Recovery Mode Active',
    recoveryModeDescription: 'Complete 3 recovery workouts to restore full Level.',
    viewWeeklyShoppingList: 'View Weekly Shopping List',
    generationFailed: 'Quest generation failed.',
    unknownError: 'Unknown error',
  },
  calendar: {
    title: 'Calendar',
    toggleSearch: 'Toggle search',
    toggleFilter: 'Toggle filter',
    toggleView: 'Toggle calendar view',
    searchPlaceholder: 'Search quests...',
    questsForDay: 'Quests for {date}',
    allScheduledQuests: 'All Scheduled Quests',
    noQuestsForDay: 'No quests scheduled for this day.',
    noQuestsFound: 'No quests found.',
    generatingAfterLevelUp: 'Generating new quests after level-up...',
    filters: {
      all: 'All',
      workout: 'Workout',
      mind: 'Mind',
      mindfulness: 'Mindfulness',
      meal: 'Meal',
    },
    errors: {
      load: 'Failed to fetch calendar data.',
      updateQuest: 'Unable to update this quest right now.',
      updated: 'Quest updated.',
      movedTo: '{quest} moved to {target}.',
      swappedWith: '{quest} swapped with {target}.',
    },
  },
  header: {
    forza: 'Forza',
    leaderboard: 'Leaderboard',
    calendar: 'Calendar',
    settings: 'Settings',
    login: 'Login',
    register: 'Register',
    navigation: 'Primary navigation',
  },
  questCard: {
    healthKitOnly: 'HealthKit only',
    moveSwap: 'Move or swap quest',
    completed: 'Completed',
    locked: 'Locked',
    viewHealthStatus: 'View health status',
    liveCoach: 'Live Coach',
    live: 'Live',
    start: 'Start',
    startMindSession: 'Start Mind session',
    verify: 'Verify',
    verifyQuest: 'Verify quest',
  },
  featureBanner: {
    dismiss: 'Dismiss guide',
    previous: 'Previous',
    next: 'Next',
    jumpTo: 'Go to feature {index}',
    items: {
      adaptiveTitle: 'Forza adapts with you',
      adaptiveDesc: 'Your quests evolve with your level, consistency, and goals.',
      missTitle: 'Do not let quests slip',
      missDesc: 'Missed quests reduce XP, so consistency keeps your momentum high.',
      coachTitle: 'AI Coach',
      coachDesc: 'Tap the AI Coach mic when you want advice or a smarter plan tweak.',
      proofTitle: 'Proof of work',
      proofDesc: 'Snap 3 meal photos to verify the quest and lock in your XP.',
      timedTitle: 'Timed workout sessions',
      timedDesc: 'Tap Verify, then Start Timer to launch a guided workout flow.',
      voiceTitle: 'Voice notes',
      voiceDesc: 'Send a voice note to verify your quest and collect the XP.',
      levelTitle: 'Level up',
      levelPrefix: 'Build streaks',
      levelSuffix: 'to unlock rewards.',
      aurasTitle: 'Earn auras',
      aurasDesc: 'Quest completion earns 100 Auras. A 7-day streak earns 1000.',
      shopTitle: 'Spend auras wisely',
      shopPrefix: 'Tap the',
      shopSuffix: 'to open the shop and activate Forza boosts.',
    },
  },
  consentGate: {
    summary:
      'Your data may be sent to external providers. Consent is required to continue using Forza.',
    hideDetails: 'Hide details',
    declineConfirmation: 'Decline confirmation',
    declineBody:
      'If you decline AI data processing consent, your account data will be deleted or anonymized and you will be signed out.',
    goBack: 'Go back',
    deleting: 'Deleting account...',
    declineDelete: 'Decline and delete account',
    saving: 'Saving consent...',
  },
  emailVerification: {
    title: 'Email verification',
    missingToken: 'No verification token found. Please check the link.',
    verifying: 'Verifying your email, please wait...',
    success: 'Verification successful. You are now signed in. Redirecting shortly...',
    unknownError: 'An unknown error occurred.',
    goToLogin: 'Go to login',
  },
  moveSwap: {
    title: 'Move / Swap',
    description: 'Change when this quest happens this week.',
    unavailable: 'Unavailable',
    cancel: 'Cancel',
    continue: 'Continue',
    confirm: 'Confirm',
    back: 'Back',
    saving: 'Saving...',
    optionMoveHere: 'Move here',
    optionSwapWith: 'Swap with {target}',
    moveTo: 'Move to {target}?',
    swapWith: 'Swap with {target}?',
    actions: {
      swap: 'Swap',
      move: 'Move',
    },
    reasons: {
      currentDay: 'Current day',
      completedQuest: 'Completed quest',
      completedWorkout: 'Completed workout',
      earlierEmptyDay: 'Earlier empty day',
      unavailable: 'Unavailable',
      noMatchingQuest: 'No matching quest',
      noMindfulnessQuest: 'No mindfulness quest',
    },
  },
  modal: {
    close: 'Close',
  },
  recoveryModal: {
    title: 'Recovery Mode Active',
    subtitle: 'Welcome Back',
    body1:
      "We noticed you've been away for a while. Instead of dropping your level, we've activated Recovery Mode.",
    body2:
      'Your level has been preserved. Before jumping back into full training, Forza suggests easing in with a few days of lighter activity such as yoga, walking, or stretching.',
    quote: "Rest is not idleness, it's the key to better performance.",
    dontShowAgain: "Don't show this again",
    cta: 'Start Recovery',
  },
  questDetails: {
    mediaReported: 'Media reported',
    reportingMedia: 'Reporting media...',
    reportInaccurateMedia: 'Report inaccurate media',
    mediaUnavailable: 'Image will be available soon',
    aiGeneratedNotice: 'AI-generated media. Read the description for accurate form.',
    expandMedia: 'Expand {label}',
    exerciseVideoAlt: 'Exercise video',
    exerciseImageAlt: 'Exercise image',
    mealImageAlt: 'Meal image',
    closeExpandedImage: 'Close expanded image',
    time: 'Time',
    calories: 'Calories',
    sets: 'Sets',
    reps: 'Reps',
    rest: 'Rest',
    seconds: '{count} seconds',
    suggestedWeight: 'Suggested Weight',
    estimatedCalories: 'Estimated Calories',
    protein: 'Protein',
    carbs: 'Carbs',
    fat: 'Fat',
    ingredients: 'Ingredients',
    instructions: 'Instructions',
    current: 'Current',
    verification: 'Verification',
    locked: 'Locked',
    automatic: 'Automatic',
    healthKitLockedBody: 'Enable HealthKit on iPhone in the Forza app to unlock this quest. Forza can then use your Apple Health data to track progress and mark the quest complete when the goal is recorded.',
    healthKitActiveBody: 'This quest uses Apple Health data for verification. Sync runs in the iPhone app on app open and when returning to the foreground.',
    imageReportedForReview: 'Image reported for review.',
    reportImageFailed: 'Failed to report image.',
    detailsTitle: '{type} Details',
    viewSession: 'View Mind Session',
    viewRecipe: 'View Recipe & Nutrition',
    viewHealth: 'View Health Verification',
    viewRoutine: 'View Full Routine',
    expandedMedia: 'Expanded quest media',
    roundsLabel: 'Rounds',
    roundLabel: 'Round {number}',
    mindArchetype: 'Logic ordering',
    mindSummary: 'Complete four short ordering rounds to lock the quest for today.',
    mindRepeatDistance: 'Exact scenarios stay out of rotation for {days} days.',
    na: 'N/A',
  },
  verificationModal: {
    checkingVoiceAvailability: 'Checking voice note availability...',
    microphoneDenied: 'Microphone access denied. Please enable it in your browser settings.',
    recordVoicePrompt: 'Please record your voice for verification.',
    verifying: 'Verifying with AI...',
    levelUp: "Level Up! You've reached Level {level}!",
    failed: 'Verification failed. Please try again.',
    mealSubmitted: 'Meal verification submitted.',
    dailySubmitted: 'Daily meal submission recorded.',
    verifyingQuestTitle: 'Verifying Quest...',
    didYouKnow: 'Did You Know?',
    seeSources: 'See sources',
    title: 'Verify Quest',
    mealDescription: 'Choose your verification method. Capture your meal or send a voice note.',
    standardDescription: 'Choose your verification method. You can record a voice note or start a guided timer.',
    startMealCapture: 'Start Meal Capture',
    checkingShort: 'Checking...',
    ultraMembers: 'Ultra members',
    unavailable: 'Unavailable',
    completeAllMeals: 'Complete all meals today',
    alreadySentToday: 'Already sent for today',
    recording: 'Recording...',
    voiceNote: 'Voice Note',
    startTimer: 'Start Timer',
    checkingFeatureAvailability: 'Checking feature availability...',
    voiceRecorded: 'Voice note recorded ({size} KB)',
    cancel: 'Cancel',
    submitVoiceNote: 'Submit Voice Note',
  },
  mindSession: {
    category: 'Mind activation',
    close: 'Close',
    unavailable: 'This Mind session is unavailable right now.',
    roundProgress: 'Round {current} of {total}',
    yourOrder: 'Your order',
    undo: 'Undo',
    clear: 'Clear',
    tapToBuild: 'Tap a tile',
    availableTiles: 'Available tiles',
    failedChecks: 'Failed checks',
    roundValue: '{value} pts max',
    checkRound: 'Check round',
    completeQuest: 'Complete Mind quest',
    saving: 'Saving...',
    finishSequence: 'Finish the sequence before checking the round.',
    tryAgain: 'Incorrect. Review your answer and try again.',
    roundCleared: 'Round cleared. Move to the next pattern.',
    lockingQuest: 'Locking today\'s Mind quest...',
    submitFailed: 'Unable to record this session right now.',
    completedToday: 'Mind activated for today.',
    completeLabel: 'Mind complete',
    completedBody: 'Today\'s Mind session is locked. Come back tomorrow for a new challenge.',
    score: 'Score',
    rounds: 'Rounds',
    perfect: 'Perfect',
    badge: 'Badge',
    zeroDrift: 'Zero Drift',
    cleanControl: 'Clean Control',
    sharpFinish: 'Sharp Finish',
    previewing: 'Memorize the live pattern before the response phase starts.',
    previewSequence: 'Live preview',
    replayPreview: 'Replay preview',
    replayPreviewCount: 'Replay preview ({used}/{total})',
    startPreview: 'Start preview',
    step: 'Step {number}',
    finishPattern: 'Finish the pattern before checking the round.',
    tryAgainPattern: 'Pattern mismatch. Rebuild it and try again.',
    selectAllTargets: 'Select every target before checking the round.',
    finishRule: 'Complete the live rule before continuing.',
    nextRuleReady: 'Rule cleared. Switch modes for the next block.',
    nextRule: 'Next rule',
    targetMiss: 'Wrong target. Keep going.',
    targetExactMatch: 'The target set is not clean. Remove distractors and try again.',
    ruleMiss: 'That item does not fit the live rule.',
    ruleExactMatch: 'The live rule is not matched cleanly. Remove the wrong picks and try again.',
    gridPrompt: 'Rebuild the flashed grid',
    ruleStage: 'Live rule set',
    targetSet: 'Target field',
    backToDashboard: 'Back to Dashboard',
    rotationStart: 'Start orientation',
    clockwise: 'Clockwise',
    counterClockwise: 'Counter-clockwise',
    completeBadge: 'Mind Complete',
    signalLadder: 'Signal ladder',
    missingStepProgress: 'Missing step {current} of {total}',
    chooseMissingStep: 'Choose the missing step before checking the round.',
    progressionMismatch: 'That step does not complete the progression. Try again.',
    mirrorAxis: 'Mirror left ↔ right',
    classifyCards: 'Classify every card',
    classifyEveryCard: 'Assign every card before checking the round.',
    classificationMismatch: 'Some cards are in the wrong set. Review both columns and try again.',
    discardTitle: 'Discard this Mind session?',
    discardBody: 'Your current progress will be lost.',
    keepPlaying: 'Keep playing',
    discard: 'Discard',
    reverseOrderHint: 'Start with the final item and work backward.',
    forwardOrderHint: 'Rebuild the sequence in the same order.',
    optionsCanRepeat: 'Options can repeat',
    modeProgress: 'Mode',
    rememberedSequence: 'Remembered sequence',
    studyTraits: 'Study the order and the traits of every card.',
    memorizeOrder: 'Memorize each item in the order shown.',
    yourFilteredSequence: 'Your filtered sequence',
    yourReverseSequence: 'Your reverse sequence',
    placeMatchingCards: 'Place matching cards here in their original order.',
    fillSlotsInOrder: 'Fill every numbered slot in order.',
    tapCardBelow: 'Tap a card below',
    candidateCards: 'Candidate cards',
    selectFilteredCards: 'Select matching cards while preserving their original order.',
    mirrorPattern: 'Mirror the pattern',
    copyOppositeSide: 'Use the source as a reference and reproduce it on the opposite side.',
    repeatFlashOrder: 'Tap the cells in the original flash order.',
    sourcePattern: 'Source pattern',
    yourMirror: 'Your mirrored pattern',
    followVisualTrait: 'Follow the card’s visual trait. The printed word may conflict.',
    chooseCategoryEveryCard: 'Choose one category for every item.',
    gridCellLabel: 'Grid cell {number}{selected}',
    gridCellSelected: ', selected',
    filterRule: 'Select only {rule}, preserving their original order.',
    warmTrait: 'Warm',
    coolTrait: 'Cool',
    roundedTrait: 'Rounded',
    angularTrait: 'Angular',
  },
  mealCapture: {
    allAnglesCaptured: 'All required angles captured.',
    cameraPreviewNotReady: 'Camera preview is not ready.',
    angleCaptured: '{angle} angle captured.',
    discardConfirm: 'Discard captured photos and exit?',
    captureAllAngles: 'Capture all three angles before submitting.',
    submitting: 'Submitting photos for verification...',
    failed: 'Meal verification failed. Please try again.',
    rearCamera: 'Rear',
    frontCamera: 'Front',
    back: 'Back',
    title: 'Meal Verification',
    deviceCamera: 'Device: {camera} camera',
    initializingCamera: 'Initializing camera...',
    cameraRequired: 'Camera access is required for meal verification. Please switch to a device with a camera.',
    captureInstruction: 'Capture Instruction',
    captureButton: 'Capture {angle}',
    switch: 'Switch',
    captureProgress: 'Capture Progress',
    angleLabel: '{angle} angle',
    captured: 'Captured',
    pending: 'Pending',
    awaitingTurn: 'Awaiting turn',
    retake: 'Retake',
    anglePreview: '{angle} preview',
    descriptionOptional: 'Description (optional)',
    descriptionPlaceholder: 'Add any details you want to share...',
    cancel: 'Cancel',
    submittingShort: 'Submitting...',
    submitPhotos: 'Submit photos',
    anglePrompts: {
      top: 'Capture the top angle first.',
      front: 'Capture the front angle next.',
      side: 'Capture the side angle last.',
    },
  },
  formCheck: {
    rearCameraTip: 'If possible, switch to the rear camera for a wider field of view.',
    accessingCamera: 'Accessing camera... please allow permission if prompted.',
    close: 'Close',
    switchCameraLens: 'Switch camera lens',
    switching: 'Switching...',
    switchLens: 'Switch lens',
    startingIn: 'Starting in',
    previewInstruction: 'Make sure your entire body fits in frame and lighting is bright. Perform the exercise for ~15 seconds after the countdown ends.',
    currentCamera: 'Current camera: {camera}',
    rear: 'Rear',
    front: 'Front',
    stopCountdown: 'Stop Countdown',
    beginCapture: 'Begin Capture',
    cancel: 'Cancel',
    capturing: 'Capturing',
    captureInstruction: 'Keep performing the movement naturally while we capture ~18 frames of your form.',
    progress: 'Progress',
    stayInFrame: "Stay within frame until the bar fills. We'll stop automatically once enough motion is captured.",
    facingModeHint: '{message} Browsers treat facingMode as a hint.',
    cancelCapture: 'Cancel Capture',
    analyzing: 'Analyzing posture... this should take just a moment.',
    couldNotComplete: 'Form check could not complete',
    poseLandmarkScore: 'Pose landmark score: {score}',
    lightingScore: 'Lighting score: {score}',
    poseQualityTip: 'Tip: Position the camera farther back so shoulders and hips stay visible.',
    qualityReadFailure:
      'We couldn’t get a consistent read on your form. Try stepping back so shoulders and hips are visible and ensure lighting is even.',
    tryAgain: 'Try Again',
    verdictPass: 'PASS',
    verdictBorderline: 'BORDERLINE',
    verdictFail: 'FAIL',
    unknown: 'UNKNOWN',
    noAdditionalTips: "We didn't receive additional tips from the provider.",
    preparingAudio: 'Preparing audio...',
    replayTips: 'Replay Tips',
    playTips: 'Play Tips',
    runAgain: 'Run Again',
    whatWeMeasured: 'What we measured',
    framesUsed: 'Frames used: {value}',
    landmarkOkRatio: 'Landmark OK ratio: {value}',
    provider: 'Provider: {value}',
    flags: 'Flags: {value}',
    none: 'none',
    uploadedBytes: 'Uploaded bytes (est.): {value} KB',
    camera: 'Camera: {value}',
    unknownLower: 'unknown',
    closeFormCheck: 'Close Form Check',
    title: 'AI Form Check',
    description: 'Record your exercise to get AI-generated observations on your form. Results are for informational purposes and do not replace professional coaching.',
    startRecording: 'Start Recording',
    privacyNote: 'We process a few camera frames with secure AI. We keep scores only and do not store images.',
    consentNote: 'By starting Form Check you consent to off-device processing for posture analysis.',
    modalTitle: 'Form Check',
    requestFailed: 'Form check failed. Please try again.',
  },
  rewardedUnlock: {
    progress: 'Unlock progress: {completed}/{required}',
    upgrade: 'Upgrade to Ultra',
    genericExtra: 'Unlock extra {targetLabel}',
    fallbackGeneric: 'Open the Forza iPhone app to unlock this bonus access.',
    voiceCoach: {
      headline: 'Tap to start your Forza AI Coach',
      body: 'Get full Voice Coach access with Ultra. Prefer a quick boost? Watch short ads to unlock 1 bonus minute.',
      button: 'Unlock 1 bonus minute',
      fallback: 'Open the Forza iPhone app to unlock a bonus minute.',
    },
    voiceNote: {
      headline: 'Need one more voice check? Unlock an extra voice note.',
      button: 'Unlock extra voice note',
    },
    mealDay: {
      headline: 'Need one more meal check-in today? Unlock a bonus check-in.',
      button: 'Unlock bonus check-in',
    },
    questReschedule: {
      headline: 'Want one more plan refresh today? Unlock an extra refresh.',
      button: 'Unlock extra refresh',
    },
    default: {
      headline: 'Unlock bonus access.',
      button: 'Unlock bonus access',
    },
  },
  aiCoach: {
    phaseStarting: 'Connecting…',
    phaseListening: 'Listening…',
    phaseTranscribing: 'Transcribing…',
    phaseThinking: 'Thinking…',
    phaseSpeaking: 'Speaking…',
    statusPreparingOnDeviceVoice: 'Preparing on-device voice…',
    statusOnDeviceVoiceReady: 'On-device voice is ready',
    cloudVoiceDescriptionFree: 'Fastest start. Free users may still need ad unlocks for short sessions.',
    cloudVoiceDescriptionPaid: 'Fastest start. Uses cloud voice instead of the local engine.',
    onDeviceVoiceReadyLabel: 'On-device voice',
    onDeviceVoiceWarmingLabel: 'On-device voice (warming…)',
    cloudVoiceLabel: 'Cloud voice',
    monthlyLimitReached: 'Monthly AI Coach limit reached.',
    fallbackFailed: 'Voice coach fallback failed. Please try again.',
    quotaLocked: 'Voice Coach locked.',
    idleTimeout: 'Session closed due to inactivity.',
    genericError: 'Voice coach error.',
    kokoroFallback: 'Kokoro failed. Falling back to server TTS.',
    micUnavailableHttp:
      'Voice Coach mic is unavailable on insecure HTTP in iOS WebView. Use HTTPS for EXPO_PUBLIC_WEB_BASE_URL and rebuild the dev client.',
    micUnavailableGeneric: 'Microphone API unavailable in this environment. Open Forza in a secure HTTPS context and retry.',
    micPermissionDenied: 'Microphone permission denied. Enable microphone access and try again.',
    micNotFound: 'No usable microphone was found. Check iPhone mic permissions and try again.',
    sessionStartFailed: 'Unable to start voice session.',
    rewardedAdsNativeOnly: 'Rewarded ads are available in the iOS app.',
    unlockPendingVerification: 'Bonus minute is almost ready. Waiting for verification...',
    openingUnlock: 'Opening unlock...',
    sessionTitle: 'Start AI Coach session',
    sessionTitleLimit: 'Monthly AI Coach limit reached.',
    statusPrefix: 'Status:',
    offlineMode: '(offline: {mode})',
    ttsPrefix: 'TTS:',
    ttsError: '(error)',
    ttsPlaying: '(playing)',
    httpFallbackActive: 'HTTP fallback active',
    limitReachedBody: "You've reached your monthly limit.",
    upgradePrompt: 'Want full access?',
    upgradeCta: 'Upgrade to Ultra',
    voiceModalTitle: 'Choose Your Voice',
    voiceModalRecommendedBadge: 'Recommended',
    voiceModalRecommendedTitle: 'Best for regular AI Coach use',
    voiceModalRecommendedBody:
      'Downloading the on-device voice is a one-time setup. After that, voice coaching lasts longer without repeated ad unlocks.',
    voiceModalDownloadTitle: 'Download Voice To This iPhone',
    voiceModalDownloadBody: 'One-time setup, around 300 MB.',
    voiceModalCloudTitle: 'Use Cloud Voice Now',
    voiceModalLater: 'Maybe later',
    readyModalTitle: 'On-Device Voice Ready',
    readyModalHeadline: 'Longer voice coaching is now available',
    readyModalBody: 'First-time setup is complete. Later on-device sessions should start much faster.',
    readyModalStartTitle: 'Start Listening',
    readyModalStartBody: 'Begin your first on-device voice session now.',
    readyModalCloudTitle: 'Use Cloud Voice Instead',
    readyModalCloudBody: 'Skip the local path for this session and start immediately with cloud voice.',
  },
  liveCoach: {
    limitReachedTitle: 'Monthly Limit Reached',
    limitReachedBody: 'You have used all your AI Coach minutes for this month. Please upgrade for more.',
    connectionErrorTitle: 'Connection Error',
    connectionUnavailable: 'Live Coach connection unavailable. Close and try again.',
    audioSendFailed: 'Live Coach audio send failed. Close and try again.',
    microphoneStartFailed: 'Live Coach microphone failed to start. Close and try again.',
    wrongQuestContext: 'Live Coach did not receive the selected quest context. Close and try again.',
    connectionClosed: 'Live Coach connection closed.',
    connectionFailed: 'Live Coach connection failed.',
    genericError: 'Live Coach error.',
    inactiveTimeout: 'Session closed due to inactivity.',
    connectFailed: 'Failed to connect.',
    sessionCreateFailed: 'Failed to create Live Coach session.',
    handshakeTimeout: 'Live Coach handshake did not complete. Restart the server and try again.',
    readyTimeout: 'Live Coach did not finish preparing the selected quest. Close and try again.',
    loading: 'Connecting to AI Coach...',
    usageCacheWarning: 'Unable to refresh usage. Using cached quota.',
    ready: 'Live Coach ready',
    preparing: 'Preparing workout...',
    connecting: 'Connecting...',
  },
  leaderboard: {
    pageTitle: 'Leaderboard',
    tabs: {
      global: 'Global',
      regional: 'Regional',
      myLeague: 'My League',
      duel: 'Duel',
    },
    loading: 'Loading leaderboard...',
    loadingDuel: 'Loading duel state...',
    loadFailed: 'Could not load data. Please try again later.',
    noData: 'No leaderboard data',
    noActiveOrPendingDuels: 'No active or pending duels.',
    globalRankings: 'Global Rankings',
    locationTitle: 'Location: {location}',
    leagueFallback: 'League',
    tierFallback: 'Tier',
    myRank: 'Your Rank: {rank} / {total}',
    currentRivalry: 'Current Rivalry',
    view: 'View',
    openDuel: 'Open Duel',
    review: 'Review',
    opponent: 'Opponent',
    you: 'You',
    youVs: 'You vs {opponent}',
    challengedYou: '{opponent} challenged you',
    youChallenged: 'You challenged {opponent}',
    timeLeft: '{time} left',
    expiresIn: 'Expires in {time}',
    sentExpiresIn: 'Sent · expires in {time}',
    lead: 'Lead: {value} XP',
    rewardOnWin: 'Reward on win: {reward}',
    incomingChallenges: 'Incoming Challenges',
    outgoingChallenges: 'Outgoing Challenges',
    recentHistory: 'Last 5',
    win: 'Win',
    loss: 'Loss',
    tie: 'Tie',
    resultVs: '{result} vs {opponent}',
    available: 'Available',
    pending: 'Pending',
    inDuel: 'In Duel',
    unavailable: 'Unavailable',
    limitReached: 'Limit Reached',
    sendChallenge: 'Send Challenge',
    acceptChallenge: 'Accept Challenge',
    decline: 'Decline',
    cancel: 'Cancel',
    working: 'Working...',
    helperDefault: '7-day duel. Score = all XP earned during the challenge.',
    helperActiveDuelUser: 'This user is already in a duel. Time remaining: {time}.',
    helperPendingIncoming: 'This user already challenged you.',
    helperPendingOutgoing: 'You already have a pending challenge with this user.',
    helperActiveDuelSelf: 'You already have an active duel. Finish it before sending a new challenge.',
    helperOutgoingLimit: 'You already have 3 pending outgoing challenges.',
    level: 'Level {level}',
    monthlyXp: 'Monthly XP: {xp}',
    lastFive: 'Last 5: {record}',
    status: 'Status: {status}',
    winnerReward: 'Winner: {reward}',
    tieReward: 'Tie: {reward}',
    locationModalTitle: 'Set Your Location',
    locationModalBody: 'To view the regional leaderboard, please provide your country.',
    locationPlaceholder: 'e.g. Canada',
    locationInvalid: 'Please enter a valid country.',
    locationSaveFailed: 'Failed to save location.',
    save: 'Save',
    savedToast: 'Location saved',
    challengeSent: 'Challenge sent',
    challengeAccepted: 'Challenge accepted',
    challengeDeclined: 'Challenge declined',
    sendChallengeFailed: 'Failed to send challenge.',
    acceptChallengeFailed: 'Failed to accept challenge.',
    declineChallengeFailed: 'Failed to decline challenge.',
    winRewardValue: '750 Auras + 200 XP',
    tieRewardValue: '375 Auras + 100 XP each',
  },
  subscription: {
    badge: 'Forza Ultra',
    heroTitle: 'Get guided workouts, faster checks, and more control.',
    heroBody:
      'Ultra is for members who want hands-free AI coaching, in-workout form checks, and more flexibility when plans change.',
    currentPlan: 'Current plan',
    active: 'Active',
    monthly: 'Monthly',
    billingNote: 'Renews monthly until cancelled. You can cancel anytime in your Apple account subscription settings.',
    refreshEntitlement: 'Refresh server entitlement',
    signInBeforePurchasing: 'Sign in before purchasing Ultra',
    nativeUnavailable: 'Open Forza on iPhone to purchase, restore, or manage Ultra with Apple IAP.',
    paymentNoticeLead: 'Payment is processed by Apple. By purchasing, you agree to the',
    and: 'and',
    actions: {
      purchase: 'Start Ultra in iOS app',
      restore: 'Already subscribed? Restore purchase',
      manage: 'Manage subscription',
    },
    status: {
      pending: '{action} started...',
      genericAction: 'Subscription action',
      successActive: 'Ultra access is active on this account.',
      successInactive: 'Purchase state refreshed. Ultra is not active yet.',
      cancelled: 'Purchase cancelled.',
      failed: 'Subscription action failed.',
      refreshing: 'Refreshing subscription status...',
      refreshFailed: 'Unable to refresh subscription status.',
    },
    planNames: {
      ultra: 'Ultra',
      freeTier: 'Free tier',
    },
    renewalLabel: 'Renews or expires on {date}',
    features: {
      handsFreeTitle: 'Hands-free AI Coach during workouts',
      handsFreeBody: 'Start voice-guided coaching without watching ads first, so you can train without staring at the phone.',
      formCheckTitle: 'Form Check inside the workout timer',
      formCheckBody: 'Launch Form Check directly from timed workout sessions when you want posture feedback mid-workout.',
      higherLimitsTitle: 'Higher verification limits across voice, meal, and form check',
      higherLimitsBody: 'Get a larger included quota for regular feedback across voice notes, meal verification, and form checks.',
      rescheduleTitle: 'More room to reschedule your plan',
      rescheduleBody: 'Adjust goals and regenerate weekly workouts more often when your schedule changes.',
    },
  },
  healthSources: {
    eyebrow: 'Evidence & Disclaimer',
    title: 'Health Sources',
    intro:
      'This page lists the public-health references behind the health tips you see in Forza, and explains which numbers inside the app are product defaults rather than personalized medical advice.',
    lastUpdated: 'Last updated:',
    medicalDisclaimerTitle: 'Medical Disclaimer',
    medicalDisclaimerBody:
      'Forza is a wellness app, not a medical device. Everything in the app - plans, targets, coaching tips, and the fact library - is for general information only and is not a substitute for professional medical advice. Talk to a qualified health professional before starting or changing any exercise, nutrition, or sleep routine, especially if you have a medical condition, injury, or are taking medication.',
    searchLabel: 'Search Fact IDs, source IDs, or keywords',
    searchPlaceholder: 'Try F001, sleep, protein, ACT-CDC...',
    referencesTitle: 'Public-Health References',
    planningDefaultsTitle: 'Forza Planning Defaults',
    planningDefaultsBody: 'These are the starting values Forza uses when building your plan. They are product defaults, not medical prescriptions.',
    factLibraryTitle: 'Fact Library & Sources',
    openSource: 'Open source',
    sourceCountOne: '{count} source',
    sourceCountOther: '{count} sources',
    defaultCountOne: '{count} default',
    defaultCountOther: '{count} defaults',
    factCountOne: '{count} fact',
    factCountOther: '{count} facts',
  },
  questGenerationLoading: {
    title: 'Building Your Plan...',
    subtitle: 'Reviewing your preferences & goals',
    alreadyRunningTitle: 'Quest Regeneration Already Running...',
    alreadyRunningSubtitle: 'Reconnecting to an existing plan build',
    restartedTitle: 'Restarting Your Plan...',
    restartedSubtitle: 'A previous stuck generation was cleared automatically',
    errorTitle: 'Quest Generation Failed',
    errorBody: 'Please try again later or contact support.',
    initiating: 'Initiating',
    finalizing: 'Finalizing',
    didYouKnow: 'Did You Know?',
    seeSources: 'See sources',
  },
  shop: {
    balanceLabel: 'Auras',
    available: 'available',
    intro: 'Use Auras to protect streaks, boost XP, and recover without losing momentum.',
    activeBoosts: 'Active boosts',
    activeCount: '{count} active',
    noActiveBoosts: 'No active boosts',
    inventoryTitle: 'Boost inventory',
    inventorySubtitle: 'Buy now, activate when the timing matters.',
    owned: 'Owned: {count}',
    activeWithTime: 'Active · {time}',
    activeItemWithTime: '{itemName} · {time}',
    buy: 'Buy',
    activate: 'Activate',
    extend: 'Extend +24h',
    buyTitle: 'Buy {itemName}',
    buyBody: 'Add 1 {itemName} to your inventory. You can activate it later.',
    activateTitle: 'Activate {itemName}',
    activateBody: 'Activate {itemName} for 24 hours and consume 1 owned item.',
    extendTitle: 'Extend {itemName}',
    extendBody: 'Extend {itemName} by 24 hours and consume 1 owned item.',
    cost: 'Cost',
    consumes: 'Consumes',
    oneItem: '1 item',
    ownedLabel: 'Owned',
    cancel: 'Cancel',
    loadFailed: 'Failed to load Auras data',
    purchaseFailed: 'Purchase failed',
    activationFailed: 'Activation failed',
    purchasedToast: '{itemName} purchased',
    activatedToast: '{itemName} activated',
    extendedToast: '{itemName} extended +24h',
    expired: 'Expired',
    items: {
      icebath: {
        name: 'Ice Bath',
        subtitle: 'Streak Freeze',
        description: 'Freeze your streak for 24h to recover.',
      },
      preworkout: {
        name: 'Pre-Workout',
        subtitle: 'XP Boost',
        description: '2x XP on quests for the next 24h.',
      },
      cheatmeal: {
        name: 'Cheat Meal',
        subtitle: 'Penalty Shield',
        description: 'No XP penalty for 24h.',
      },
    },
  },
  shoppingList: {
    loadFailed: 'Could not load grocery list.',
    title: '7-Day Grocery List',
    subtitle: "Generated from this week's meals",
    close: 'Close shopping list',
    loading: 'Extracting ingredients...',
    empty: 'No upcoming meal quests found for this week.',
    syncing: 'Syncing...',
    toggleItem: 'Toggle {item}',
  },
  dailyMealCheckin: {
    meals: {
      breakfast: 'Breakfast',
      lunch: 'Lunch',
      dinner: 'Dinner',
      snack: 'Snack / Other',
    },
    earnedToday: 'Earned Today',
    aiAnalysis: 'AI Analysis',
    matchScore: '{value}% MATCH',
    calories: 'Calories',
    protein: 'Protein',
    carbs: 'Carbs',
    fat: 'Sat. Fat',
    draftSaved: 'Draft saved locally.',
    tooLong: '{meal} exceeds {max} characters.',
    requiredUnlessCompleted: '{meal} is required unless already completed.',
    checkingAvailability: 'Checking daily meal check-in availability...',
    unavailable: 'Daily meal check-in unavailable right now.',
    analyzingMeals: 'Analyzing meals...',
    submitFailed: 'Failed to submit. Please try again.',
    completed: 'Completed',
    placeholder: 'What did you have for {meal}?',
    dailySummary: 'Daily Summary',
    title: 'Daily Meal Check-in',
    levelUp: 'Level Up!',
    welcomeLevel: 'Welcome to Level {level}',
    continue: 'Continue',
    saveDraft: 'Save Draft',
    checkingShort: 'Checking...',
    alreadySent: 'Already Sent',
    sendDailyCheckin: 'Send Daily Check-in',
  },
  timerSession: {
    rest: 'Rest',
    workout: 'Workout',
    recovery: 'Recovery',
    next: 'Next',
    seconds: '{count} seconds',
    backToVerification: 'Back to verification',
    workoutTracker: 'Workout Tracker',
    timerPaused: 'Timer paused',
    done: 'Done',
    noTrackableExercises: 'No trackable exercises are available for this quest.',
    targetValue: 'Target {value}',
    setNumber: 'Set {number}',
    setProgress: 'Set {current}/{total}',
    reps: 'Reps',
    weightUnit: 'Weight {unit}',
    submittingVerification: 'Submitting verification...',
    errorOccurred: 'An error occurred',
    tryAgain: 'Try Again',
    statSet: 'Set',
    statTimer: 'Timer',
    statTarget: 'Target',
    pause: 'Pause',
    resume: 'Resume',
    tracker: 'Tracker',
    skip: 'Skip',
    startFormCheck: 'Start Form Check',
    upNext: 'Up Next',
    restLabel: 'Rest {value}',
    loading: 'Loading timer...',
    wakeLockUnavailable: 'Screen wake lock unavailable; your device may sleep during the timer.',
    formCheckLimitReached: 'Form Check daily limit reached.',
  },
  profileEditor: {
    saveTraining: 'Save training settings',
    saveProfile: 'Save profile only',
    saving: 'Saving...',
    addCustomEquipment: 'Add custom equipment',
    add: 'Add',
    cancel: 'Cancel',
    optionalNotes: 'Optional notes',
    allergiesCheckbox: 'I have allergies',
    allergiesPlaceholder: 'List allergies or reactions.',
    validationWorkoutDays: 'Choose exactly {expected} workout day{suffix} before saving.',
    modeTraining: {
      title: 'Training Settings',
      description: 'Update exercise familiarity, weekly activity, sessions per week, and preferred workout days only.',
      badgeLabel: 'Training focus',
    },
    modeFull: {
      title: 'Profile Editor',
      description: 'Saving here updates your profile without automatically rebuilding your quest plan.',
      badgeLabel: 'Edit mode',
    },
    modeRequired: {
      title: 'Profile Editor',
      description: 'Saving here updates your profile without automatically rebuilding your quest plan.',
      badgeLabel: 'Profile required',
    },
  },
};
